<template>
  <div class="p-5">
    <span class="block text-xs text-black font-semibold mb-2">
      Workflow
    </span>

    <ol>
      <li
        v-for="(step, index) in workflowSteps"
        :key="index"
        class="text-xs font-medium leading-6"
      >
        <span :class="{ 'text-success': isComplete(index) }">
          <FontAwesomeIcon
            :icon="faCheckCircle"
            class="text-base mr-2"
            :class="isComplete(index) ? 'text-success' : 'text-chrome-light'"
          />
          {{ index + 1 }}. {{ step.label }}
        </span>

        <span
          v-if="isInProgress(index)"
          class="bg-warning text-white px-1.5 pt-[2px] pb-[3px] rounded text-2xs ml-2"
        >
          in progress
        </span>

        <template v-if="showDownload(step, index)">
          <template v-if="planPdfs?.[step.pdfStep]?.status === 'complete'">
            <span class="mx-1">|</span>

            <button
              v-if="planPdfs?.[step.pdfStep]?.status === 'complete'"
              class="!font-normal"
              @click="$emit('download', step.pdfStep)"
            >
              <span class="!text-[#2E88F7]">
                {{ step.download }}

                <FontAwesomeIcon
                  :icon="faArrowDownToLine"
                  class="mr-0.5"
                />
              </span>
            </button>
          </template>

          <span
            v-else-if="planPdfs?.[step.pdfStep]?.status === 'pending'"
            class="inline-block !font-normal text-chrome-mid italic"
          >
            Generating PDF... this can take a few minutes, check back later
          </span>

          <span
            v-else-if="planPdfs?.[step.pdfStep]?.status === 'failed'"
            class="!font-normal text-danger"
            :class="step.workflowStep === null ? 'block ml-6' : 'inline-block'"
          >
            PDF Generation failed

            <span class="text-black mx-1">|</span>

            <button
              class="!font-normal"
              @click="$emit('regenerate', step.pdfStep)"
            >
              <span class="!text-[#2E88F7]">
                Regenerate

                <FontAwesomeIcon
                  :icon="faArrowsRotate"
                  class="ml-0.5"
                />
              </span>
            </button>
          </span>
        </template>
      </li>
    </ol>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { faArrowDownToLine, faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';

import { PlanWorkflowStep, PlanWorkflowStepOrder } from '@/const';

const props = defineProps({
  planPdfs: {
    type: Object,
    default: () => ({}),
  },
  workflowStep: {
    type: String,
    required: true,
  },
  pdfsLoading: Boolean,
});

defineEmits(['download', 'regenerate']);

// "Published as Draft" has no corresponding API state — it's a display-only milestone that sits
// between DraftApproval and WorkInProgress3. workflowStep: null flags it as display-only, and
// pdfStep points to WorkInProgress3 — the destination of the approve_draft_approval transition that
// captures the published-draft snapshot.
const workflowSteps = [
  { label: 'Work in Progress', workflowStep: PlanWorkflowStep.WorkInProgress1 },
  { label: 'Submitted for Council Noting', download: 'Download', pdfStep: PlanWorkflowStep.SubmittedForCouncilNoting, workflowStep: PlanWorkflowStep.SubmittedForCouncilNoting },
  { label: 'Submitted for MEC Review', workflowStep: PlanWorkflowStep.SubmittedForMecReview },
  { label: 'Work in Progress', workflowStep: PlanWorkflowStep.WorkInProgress2 },
  { label: 'Draft Approval', workflowStep: PlanWorkflowStep.DraftApproval },
  { label: 'Published as Draft', download: 'Download Published Draft', pdfStep: PlanWorkflowStep.WorkInProgress3, workflowStep: null },
  { label: 'Work in Progress', workflowStep: PlanWorkflowStep.WorkInProgress3 },
  { label: 'Submitted for Council Approval', download: 'Download', pdfStep: PlanWorkflowStep.SubmittedForCouncilApproval, workflowStep: PlanWorkflowStep.SubmittedForCouncilApproval },
  { label: 'Submitted to MEC', workflowStep: PlanWorkflowStep.SubmittedToMec },
  { label: 'Final Approval', workflowStep: PlanWorkflowStep.FinalApproval },
  { label: 'Published as Final', workflowStep: PlanWorkflowStep.PublishedAsFinal },
];

const currentStepPosition = computed(() => PlanWorkflowStepOrder.indexOf(props.workflowStep));

const isComplete = (index) => {
  const step = workflowSteps[index];
  // The display-only step has no position in the order, so fall back to DraftApproval —
  // it becomes complete as soon as the plan advances past that point.
  const position = PlanWorkflowStepOrder.indexOf(step.workflowStep ?? PlanWorkflowStep.DraftApproval);
  // PublishedAsFinal is the last step in the order, so it can't satisfy a strict less-than
  // against itself — handled explicitly so the final step also renders as complete.
  return position < currentStepPosition.value || props.workflowStep === PlanWorkflowStep.PublishedAsFinal;
};

const isInProgress = (index) => {
  const step = workflowSteps[index];
  // Display-only steps (workflowStep: null) can never be in progress, and at PublishedAsFinal
  // the entire timeline shows as complete rather than marking the last step as in progress.
  return step.workflowStep !== null && step.workflowStep === props.workflowStep && props.workflowStep !== PlanWorkflowStep.PublishedAsFinal;
};

// At PublishedAsFinal, downloads move to the WorkflowActions panel, so they're suppressed here.
const showDownload = (step, index) => step.download && isComplete(index) && props.workflowStep !== PlanWorkflowStep.PublishedAsFinal && !props.pdfsLoading;
</script>
